/** tm/components/GovFeedParts.tsx — 공고 검토함 조각들 (2026-09-30)
 *
 *  GovPage 와 같은 토큰(C·T·R)·Badge·DDayBadge 만 쓴다 — 새 시각 언어를 만들지 않는다.
 *  · GovViewSwitch / GovTopBar : GovPage 상단 「등록된 공고 | 공고 검토함 | 단톡방 공유 | AI 점검」 전환
 *  · BUCKETS · programOf …      : 대량 검토 표의 탭 · 행 요약 (서버 필드가 없을 때 대신 판단)
 *  · FeedPopover               : 표 칸 인라인 편집 팝오버 (body 로 portal)
 *  · GovFeedAiBox              : 3열 AI 요약 · 「AI가 찾은 근거」 (확신 낮은 칸은 연노랑 + 확인 필요)
 *  · parseBizinfoExcel         : 기업마당 목록 엑셀 → import 행 (브라우저에서 xlsx 로 파싱)
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import * as XLSX from 'xlsx';
import { C, T, R, Badge, DDayBadge } from './ui';
import { dDay, today } from '../hooks';
import type { GovFeedRow, GovFeedScreen, GovFeedAiResult, GovFeedImportRow, GovFeedBucket, GovProgram, GovFeedUsage } from '../types';
import { conditionsSummary, tracksSummary } from './govRuleText';

// ── 상단 전환 ────────────────────────────────────────────────────
export type GovView = 'list' | 'inbox' | 'share' | 'check';

export function GovViewSwitch({ view, onChange, registered, inbox, share = null }: {
  view: GovView; onChange: (v: GovView) => void; registered: number | null; inbox: number | null; share?: number | null;
}) {
  const tabs: { k: GovView; label: string; n: number | null }[] = [
    { k: 'list', label: '등록된 공고', n: registered },
    { k: 'inbox', label: '공고 검토함', n: inbox },
    { k: 'share', label: '단톡방 공유', n: share },
    { k: 'check', label: 'AI 점검', n: null },   // 2026-10-08 AI 판정 점검(시험 세트 · 무작위 표본) — pages/GovTargetCheckPage.tsx
  ];
  return (
    <div role="tablist" style={{ display: 'inline-flex', padding: 3, gap: 2, borderRadius: R.md, background: '#E9EBEF', flexShrink: 0 }}>
      {tabs.map(t => {
        const act = view === t.k;
        return (
          <button key={t.k} role="tab" aria-selected={act} onClick={() => onChange(t.k)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
              background: act ? C.white : 'transparent', boxShadow: act ? '0 1px 2px rgba(17,24,39,0.10)' : 'none',
              color: act ? C.text : C.sub, fontSize: T.sm, fontWeight: act ? 700 : 500, whiteSpace: 'nowrap',
            }}>
            {t.label}
            {t.n != null && <span style={{ fontSize: T.xs, fontWeight: 700, color: act ? C.accent : C.muted, fontVariantNumeric: 'tabular-nums' }}>{t.n.toLocaleString()}</span>}
          </button>
        );
      })}
    </div>
  );
}

export function GovTopBar({ left, children }: { left: React.ReactNode; children?: React.ReactNode }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', minHeight: 50, boxSizing: 'border-box',
      borderBottom: `1px solid ${C.border}`, flexShrink: 0, minWidth: 0,
    }}>
      {left}
      {children}
    </div>
  );
}

// ── 판정 · 상태 배지 ─────────────────────────────────────────────
export const AMBER = { bg: '#FFFBEB', border: '#FDE68A', text: '#92400E', chipBg: '#FEF3C7' } as const;

export const SCREEN_LABEL: Record<GovFeedScreen, string> = { fit: '추천', maybe: '애매', unfit: '비추천', pending: '미판정' };
export const SCREEN_COLOR: Record<GovFeedScreen, [string, string]> = {
  fit: [C.greenLt, C.green], maybe: [AMBER.chipBg, '#B45309'], unfit: [C.bgAlt, C.muted], pending: [C.bgAlt, C.muted],
};

export function ScreenBadge({ screen, fit }: { screen: GovFeedScreen; fit?: number | null }) {
  const [bg, color] = SCREEN_COLOR[screen] || SCREEN_COLOR.pending;
  return (
    <Badge bg={bg} color={color} style={{ border: screen === 'unfit' || screen === 'pending' ? `1px solid ${C.border}` : '1px solid transparent', padding: '2px 8px' }}>
      {SCREEN_LABEL[screen] || '미판정'}{fit != null ? <span style={{ fontWeight: 700, marginLeft: 4 }}>{fit}점</span> : null}
    </Badge>
  );
}

const isRecurring = (years: string | null | undefined) => String(years || '').split(',').filter(Boolean).length >= 2;

/** 마감 표시 — 게시 중이면 D-day, 지난 반복 사업이면 「내년 예상」, 종료일 없음 = 상시 */
export function FeedDeadline({ end, years }: { end: string | null; years: string }) {
  if (!end) return <Badge bg={C.accentLt} color={C.accent}>상시</Badge>;
  const d = dDay(end);
  if (d !== null && d < 0) {
    return isRecurring(years) ? <Badge bg={C.purpleLt} color={C.purple}>내년 예상</Badge> : <Badge bg={C.bgAlt} color={C.muted}>마감</Badge>;
  }
  return <DDayBadge deadline={end} />;
}

// ── AI 요약 · 근거 ──────────────────────────────────────────────
type A = GovFeedAiResult;
const OP_WORD: Record<string, string> = { '<': '미만', '<=': '이하', '>=': '이상', '>': '초과' };
const num = (v: number | null | undefined) => (v != null && Number.isFinite(Number(v)) ? Number(v).toLocaleString() : '');
/** 업력 표시 — 소수면 개월로 (0.5 → 6개월 · 1.5 → 1년 6개월 · 3 → 3년) — 엔진 govMatch.yrsTxt 와 같다 (2026-10-03) */
export const yrsTxt = (v: number): string => {
  const m = Math.round(Number(v) * 12);
  if (!Number.isFinite(m)) return String(v);
  if (m % 12 === 0) return `${m / 12}년`;
  const y = Math.floor(m / 12), mo = m % 12;
  return `${y ? `${y}년 ` : ''}${mo}개월`;
};
const yrsRange = (lo: number | null, hi: number | null): string => {
  if (lo == null && hi == null) return '';
  if (lo != null && hi != null) return `${yrsTxt(lo)}~${yrsTxt(hi)}`;
  return lo != null ? `${yrsTxt(lo)} 이상` : `${yrsTxt(hi as number)} 이하`;
};
const range = (lo: number | null, hi: number | null, unit: string, pre = '') => {
  if (lo == null && hi == null) return '';
  if (lo != null && hi != null) return `${pre}${num(lo)}~${num(hi)}${unit}`;
  return lo != null ? `${pre}${num(lo)}${unit} 이상` : `${pre}${num(hi)}${unit} 이하`;
};

/** AI_RESULT 칸 → 폼 항목 (짝 칸은 한 줄로) — 순서는 GovProgramForm 과 같다 */
export const EVIDENCE_ROWS: { label: string; keys: (keyof A)[]; fmt: (a: A) => string }[] = [
  { label: '사업명', keys: ['name'], fmt: a => a.name || '' },
  { label: '리포트 사업명', keys: ['report_name'], fmt: a => a.report_name || '' }, // 2026-10-01 — 이전 분석 결과엔 없음(빈 줄은 상자가 건너뛴다)
  { label: '마감일', keys: ['deadline', 'firstCome'], fmt: a => (a.firstCome ? '선착순' : a.deadline || '') },
  { label: '신청 시작일', keys: ['applyStart'], fmt: a => a.applyStart || '' },
  { label: '매년 반복', keys: ['recurYearly'], fmt: a => (a.recurYearly ? '예' : '아니오') },
  { label: '지원금액', keys: ['amount', 'amountMaxManwon'], fmt: a => a.amount || (a.amountMaxManwon != null ? `${num(a.amountMaxManwon)}만원` : '') },
  { label: '수행기관', keys: ['agency'], fmt: a => a.agency || '' },
  { label: '지역', keys: ['regions', 'regionDetails'], fmt: a => [...(a.regions || []), ...(a.regionDetails || [])].join(' · ') || '전국' },
  { label: '업종', keys: ['bizTypes'], fmt: a => (a.bizTypes || []).join(' · ') || '무관' },
  { label: '기업규모', keys: ['companyScale'], fmt: a => a.companyScale || '무관' },
  { label: '창업기업', keys: ['startup'], fmt: a => a.startup || '무관' },
  { label: '대표 만나이', keys: ['ceoAgeMin', 'ceoAgeMax'], fmt: a => range(a.ceoAgeMin, a.ceoAgeMax, '세', '만 ') || '무관' },
  { label: '연매출', keys: ['salesOp', 'salesEok'], fmt: a => (a.salesEok != null && a.salesOp ? `${num(a.salesEok)}억원 ${OP_WORD[a.salesOp] || a.salesOp}` : '무관') },
  { label: '상시근로자', keys: ['empOp', 'empN'], fmt: a => (a.empN != null && a.empOp ? `${num(a.empN)}명 ${OP_WORD[a.empOp] || a.empOp}` : '무관') },
  { label: '업력', keys: ['yrsMin', 'yrsMax'], fmt: a => yrsRange(a.yrsMin, a.yrsMax) || '무관' },
  { label: '가점', keys: ['wantPatent', 'wantTm'], fmt: a => [a.wantPatent ? '특허' : '', a.wantTm ? '상표' : ''].filter(Boolean).join(' · ') || '없음' },
  // 2026-10-05 — 확장 조건·세부 유형 (이전 분석 결과엔 없음 → 빈 줄은 상자가 건너뛴다)
  { label: '추가 조건', keys: ['conditions'], fmt: a => conditionsSummary(a.conditions) || '없음' },
  { label: '분류', keys: ['category'], fmt: a => (a.category || []).join(' · ') },
  { label: '전용 대상', keys: ['targets'], fmt: a => (a.targets || []).join(' · ') },   // 2026-10-05f — 빈 줄은 상자가 건너뛴다
  { label: '세부 유형', keys: ['tracks'], fmt: a => tracksSummary(a.tracks) || '없음' },
];

/** 확인이 필요한 폼 항목 이름들 */
export function unsureLabels(ai: A | null): string[] {
  if (!ai) return [];
  const u = new Set(ai.unsure || []);
  return EVIDENCE_ROWS.filter(r => r.keys.some(k => u.has(k as string))).map(r => r.label);
}

const FILE_WORD: Record<string, string> = { hwp: '공고문(HWP)', hwpx: '공고문(HWPX)', pdf: '공고문(PDF)', image: '공고문(그림)', docx: '공고문(Word)' };

const boxLabel: React.CSSProperties = { width: 56, flexShrink: 0, fontSize: 11, fontWeight: 600, color: C.sub, paddingTop: 1 };

/** 근거 인용 (첫 조각) — 원문 형광 찾기용 */
export function evidenceQuote(ai: A | null, label: string): string {
  if (!ai) return '';
  const row = EVIDENCE_ROWS.find(r => r.label === label);
  const ev = ai.evidence || {};
  return row ? (row.keys.map(k => ev[k as string]).find(Boolean) || '') : '';
}

export function GovFeedAiBox({ ai, screen, open, onToggle, onQuote }: {
  ai: A; screen: GovFeedScreen; open: boolean; onToggle: () => void;
  /** 근거를 누르면 원문에서 찾아 형광 */
  onQuote?: (quote: string, label: string) => void;
}) {
  const ev = ai.evidence || {};
  const u = new Set(ai.unsure || []);
  const rows = EVIDENCE_ROWS
    .map(r => ({ ...r, quote: Array.from(new Set(r.keys.map(k => ev[k as string]).filter(Boolean))).join(' / '), unsure: r.keys.some(k => u.has(k as string)) }))
    .filter(r => r.quote || r.unsure);
  const src = ai.source;
  const fileWord = src?.file === 'pdf' && src.pdfMode ? (src.pdfMode === 'text' ? '공고문(PDF 글)' : '공고문(PDF 그림·스캔)') : src?.file ? FILE_WORD[src.file] || src.file : '';
  const srcText = src ? [src.detail ? '상세 페이지' : '', fileWord].filter(Boolean).join(' + ') + (src.batch ? ' · 일괄 분석' : '') : '';
  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: R.md, background: C.white, overflow: 'hidden' }}>
      <style>{'.gf-ev .gf-ev-go{opacity:0;transition:opacity .12s}.gf-ev:hover .gf-ev-go{opacity:1}.gf-ev:hover{filter:brightness(0.985)}'}</style>
      <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
          <ScreenBadge screen={screen} />
          {ai.fit != null && <span style={{ fontSize: T.sm, fontWeight: 700, color: C.text }}>적합도 {ai.fit}</span>}
          {ai.fitReason && <span style={{ fontSize: T.sm, color: C.sub, lineHeight: 1.5, flex: 1, minWidth: 200 }}>{ai.fitReason}</span>}
          {src?.pdfMode && (
            <span title={src.pdfMode === 'text' ? 'PDF 에서 뽑은 글을 읽고 분석했습니다' : '글이 없는 PDF(스캔·그림)라 쪽 그림을 보고 분석했습니다 — 숫자·날짜를 한 번 더 확인하세요'}
              style={{ fontSize: 10.5, fontWeight: 600, padding: '1px 6px', borderRadius: 4, whiteSpace: 'nowrap', background: src.pdfMode === 'text' ? C.bg : AMBER.chipBg, color: src.pdfMode === 'text' ? C.sub : AMBER.text }}>
              {src.pdfMode === 'text' ? '원문: PDF 글' : '원문: PDF 그림(스캔)'}
            </span>
          )}
        </div>
        {ai.summary && (
          <div style={{ display: 'flex', gap: 8 }}>
            <span style={boxLabel}>지원내용</span>
            <span style={{ fontSize: T.sm, color: C.text, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{ai.summary}</span>
          </div>
        )}
        {ai.targetText && (
          <div style={{ display: 'flex', gap: 8 }}>
            <span style={boxLabel}>지원대상</span>
            <span style={{ fontSize: T.sm, color: C.text, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{ai.targetText}</span>
          </div>
        )}
      </div>

      {rows.length > 0 && (
        <div style={{ borderTop: `1px solid ${C.border}` }}>
          <div onClick={onToggle} style={{ padding: '8px 14px', background: C.bgAlt, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: C.sub }}>
              AI가 찾은 근거 <span style={{ fontWeight: 400, color: C.muted }}>{rows.length}개 항목</span>
              {u.size > 0 && <span style={{ marginLeft: 6, fontWeight: 600, color: AMBER.text }}>· 확인 필요 {rows.filter(r => r.unsure).length}</span>}
            </span>
            <span style={{ fontSize: 11, color: C.muted }}>{open ? '▾ 접기' : '▸ 펼치기'}</span>
          </div>
          {open && (
            <div>
              {rows.map((r, i) => {
                const val = r.fmt(ai);
                const first = r.keys.map(k => ev[k as string]).find(Boolean) || '';
                const click = onQuote && first ? () => onQuote(first, r.label) : undefined;
                return (
                  <div key={r.label} className={click ? 'gf-ev' : undefined} onClick={click} title={click ? '원문에서 보기' : undefined} style={{
                    display: 'flex', gap: 8, padding: '8px 14px', background: r.unsure ? AMBER.bg : C.white, cursor: click ? 'pointer' : 'default',
                    borderTop: i ? `1px solid ${r.unsure ? AMBER.border : C.border}` : 'none',
                  }}>
                    <span style={{ ...boxLabel, width: 72 }}>{r.label}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: T.sm, fontWeight: 600, color: val ? C.text : C.muted }}>{val || '비어 있음'}</span>
                        {r.unsure && <Badge bg={AMBER.chipBg} color={AMBER.text} style={{ padding: '1px 7px', fontSize: 10 }}>확인 필요</Badge>}
                        {click && <span className="gf-ev-go" style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 600, color: C.accent }}>원문에서 보기 →</span>}
                      </div>
                      <div style={{ fontSize: 11, color: r.quote ? C.sub : C.muted, marginTop: 3, lineHeight: 1.5 }}>
                        {r.quote ? `“${r.quote}”` : '공고문에서 근거를 못 찾음'}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {(srcText || (src && src.notes && src.notes.length > 0)) && (
        <div style={{ padding: '7px 14px', borderTop: `1px solid ${C.border}`, fontSize: 11, color: C.muted, lineHeight: 1.5 }}>
          {srcText && <>읽은 자료: {srcText}{src && src.chars ? ` · ${src.chars.toLocaleString()}자` : ''}</>}
          {src && src.notes && src.notes.length > 0 && <div>{src.notes.join(' · ')}</div>}
        </div>
      )}
    </div>
  );
}

// ── 엑셀 ────────────────────────────────────────────────────────
const CUTOFF = '2025-01-01';
const COLS: Record<keyof GovFeedImportRow, string> = {
  no: '번호', ministry: '소관부처', agency: '사업수행기관', field: '지원분야', title: '공고명',
  start: '신청시작일자', end: '신청종료일자', registered: '등록일자', url: '공고상세URL',
};

/** 날짜 칸 → 'YYYY-MM-DD' (판단 못 하면 null — 서버가 다시 거른다) */
function ymdOf(v: unknown): string | null {
  if (v == null || v === '') return null;
  if (typeof v === 'number' && v > 20000 && v < 80000) return new Date(Math.round((v - 25569) * 86400000)).toISOString().slice(0, 10);
  const m = String(v).match(/(\d{4})\D?(\d{1,2})\D?(\d{1,2})/);
  return m ? `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}` : null;
}
const cell = (v: unknown): string | number | null => (v == null || v === '' ? null : typeof v === 'number' ? v : String(v).trim());

/** 기업마당 목록 엑셀 → import 행 (등록일 2025-01-01 이후만) */
export async function parseBizinfoExcel(file: File): Promise<{ rows: GovFeedImportRow[]; totalRows: number; old: number }> {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array' });
  const ws = wb.Sheets[wb.SheetNames[0]];
  if (!ws) throw new Error('시트를 찾을 수 없습니다');
  const aoa = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, raw: true, defval: '' });
  const hIdx = aoa.slice(0, 15).findIndex(r => Array.isArray(r) && r.some(c => String(c).trim() === '공고명'));
  if (hIdx < 0) throw new Error('기업마당 목록 엑셀이 아닙니다 (「공고명」 열 없음)');
  const header = (aoa[hIdx] as unknown[]).map(c => String(c).replace(/\s+/g, ''));
  const idx = {} as Record<keyof GovFeedImportRow, number>;
  (Object.keys(COLS) as (keyof GovFeedImportRow)[]).forEach(k => { idx[k] = header.indexOf(COLS[k]); });
  if (idx.title < 0 || idx.url < 0) throw new Error('「공고명」「공고상세URL」 열이 필요합니다');
  const rows: GovFeedImportRow[] = [];
  let totalRows = 0;
  let old = 0;
  for (const r of aoa.slice(hIdx + 1)) {
    if (!Array.isArray(r)) continue;
    const get = (k: keyof GovFeedImportRow) => (idx[k] >= 0 ? r[idx[k]] : '');
    const title = String(get('title') || '').trim();
    const url = String(get('url') || '').trim();
    if (!title || !url) continue;
    totalRows += 1;
    const reg = ymdOf(get('registered'));
    if (reg && reg < CUTOFF) { old += 1; continue; }
    rows.push({
      no: cell(get('no')) ?? '', ministry: String(get('ministry') || '').trim(), agency: String(get('agency') || '').trim(),
      field: String(get('field') || '').trim(), title, url,
      start: cell(get('start')), end: cell(get('end')), registered: cell(get('registered')),
    });
  }
  return { rows, totalRows, old };
}

// ── 대량 검토: 탭(bucket) · 행 요약 ───────────────────────────────
export const PAST_NOTE = '마감된 일회성 공고 — 자동 분석하지 않음 · 필요한 건 행을 열어 「지금 분석」';
export const BUCKETS: { k: GovFeedBucket; label: string; dot: string; tip?: string }[] = [
  { k: 'ready', label: '바로 등록 가능', dot: C.green },
  { k: 'check', label: '확인 필요', dot: '#D97706' },
  { k: 'maybe', label: '애매', dot: '#CA8A04' },
  { k: 'unfit', label: '비추천', dot: C.borderDk },
  { k: 'pending', label: '분석 대기', dot: C.muted },
  { k: 'past', label: '지난 공고', dot: C.muted, tip: PAST_NOTE }, // 2026-10-01 예산 — 마감된 일회성은 자동 분석 안 함
];

/** 행의 탭 — 서버(prefill.bucketOf)가 정한 값. 분석 직후처럼 비어 있으면 '분석 대기' 로 본다 */
export function bucketOf(r: GovFeedRow): GovFeedBucket {
  return r.bucket || (r.screen === 'unfit' ? 'unfit' : r.aiStatus === 'past' ? 'past' : 'pending');
}

export const SOURCE_LABEL: Record<string, string> = { bizinfo: '기업마당', sbiz24: '소상공인24', naver: '네이버', fsc: '금융위', kodit: '신보', kibo: '기보', excel: '기업마당', api: '기업마당' };

/** 배열 · 쉼표 문자열 → 배열 */
export function toList(v: string[] | string | null | undefined): string[] {
  if (Array.isArray(v)) return v.map(x => String(x).trim()).filter(Boolean);
  return String(v || '').split(',').map(x => x.trim()).filter(Boolean);
}

// ── 지역 (2026-10-05 지도사 「서울에도 보이고 경기에도 보이고 인천에도 보이게」) ──────────
/** 지역 순서 — 전국 → 수도권(서울·경기·인천) → 그 밖 광역(지도 순서). 검토함 지역 칸 · 등록된 공고 정렬 · backend govFeed/facets.js 와 같다 */
export const REGION_ORDER = ['서울', '경기', '인천', '부산', '대구', '광주', '대전', '울산', '세종', '강원', '충북', '충남', '전북', '전남', '경북', '경남', '제주'];
/** 지역을 정하지 않은(전국) 공고의 칸 이름 */
export const NATIONWIDE = '전국';
/** 긴 이름 → 줄인 이름 (서울특별시·경기도·충청북도·강원특별자치도 …). 경기는 경남·경북보다 먼저 */
const REGION_LONG: [RegExp, string][] = [
  [/^서울/, '서울'], [/^부산/, '부산'], [/^대구/, '대구'], [/^인천/, '인천'], [/^광주/, '광주'], [/^대전/, '대전'], [/^울산/, '울산'], [/^세종/, '세종'],
  [/^경기/, '경기'], [/^강원/, '강원'], [/^충(청)?북/, '충북'], [/^충(청)?남/, '충남'], [/^전(라)?북/, '전북'], [/^전(라)?남/, '전남'],
  [/^경(상)?북/, '경북'], [/^경(상)?남/, '경남'], [/^제주/, '제주'],
];
/** 지역 칸 → 광역 목록. 저장값은 쉼표 문자열(「서울,경기,인천」)이지만 배열 · 가운뎃점(·) · 빗금(/) · 공백 변형도 받는다.
 *  빈 값 = 전국(빈 배열) · 「무관」「전체」 토막은 버린다 · 「전국」 이 섞여 있으면 전국(services/govShare.js roomsFor 와 같은 규칙)
 *  긴 이름은 줄인 이름으로 · 중복 제거 · 모르는 이름은 그대로 둔다 */
export function splitRegions(v: string[] | string | null | undefined): string[] {
  const parts = Array.isArray(v) ? v.map(x => String(x == null ? '' : x)) : String(v || '').split(/[,·/|\s]+/);
  const out: string[] = [];
  for (const p of parts) {
    const t = p.replace(/\s+/g, '');
    if (!t || t === '무관' || t === '전체') continue;
    if (t === NATIONWIDE) return [];
    const hit = REGION_LONG.find(([re]) => re.test(t));
    const r = hit ? hit[1] : t;
    if (!out.includes(r)) out.push(r);
  }
  return out;
}
/** 광역 이름의 정렬 순위 — REGION_ORDER 에 없는 이름은 맨 뒤 */
export function regionRank(r: string): number { const i = REGION_ORDER.indexOf(r); return i < 0 ? REGION_ORDER.length : i; }

/** 행 표시용 등록값 (정규화) */
export interface FeedProg {
  /** reportName: 리포트 사업명 (2026-10-01) — 지난 공고는 이것이 주 이름 */
  name: string; reportName: string; regions: string[]; regionDetails: string[]; category: string[];
  amount: string; amountMaxManwon: number | null;
  applyStart: string | null; deadline: string | null; firstCome: boolean; recurYearly: boolean;
  companyScale: string; startup: string; yrsMin: number | null; yrsMax: number | null; ceoAgeMax: number | null;
}

/** 지난 공고인가 (행 요약) — 마감 < 오늘 · 선착순 아님. 등록 양식 isPastGovForm 과 같은 규칙 */
export function isPastProg(p: Pick<FeedProg, 'deadline' | 'firstCome'>): boolean {
  return !p.firstCome && !!p.deadline && String(p.deadline).slice(0, 10) < today();
}

/** 표에 보일 이름 — 지난 공고는 리포트 사업명(FP 이름이 필요 없다), 그 밖은 FP 사업명 */
export function displayNameOf(p: FeedProg): string {
  return (isPastProg(p) && p.reportName) || p.name;
}

export function programOf(r: GovFeedRow): FeedProg {
  const p = r.program || {};
  const hint = String(r.regionHint || '').trim();
  return {
    name: String(p.name || r.aiName || r.baseName || r.title || ''),
    reportName: String(p.reportName || ''),
    regions: p.regions != null ? toList(p.regions) : (hint && hint !== '전국' ? [hint] : []),
    regionDetails: toList(p.regionDetails),
    category: toList(p.category),
    amount: String(p.amount || ''), amountMaxManwon: p.amountMaxManwon ?? null,
    applyStart: p.applyStart ?? null, deadline: p.deadline ?? (r.program ? null : r.latestEnd),
    firstCome: !!p.firstCome, recurYearly: p.recurYearly ?? String(r.years || '').includes(','),
    companyScale: String(p.companyScale || ''), startup: String(p.startup || ''),
    yrsMin: p.yrsMin ?? null, yrsMax: p.yrsMax ?? null, ceoAgeMax: p.ceoAgeMax ?? null,
  };
}

/** 저장 payload(부분) → 행 요약에 덮을 값 */
export function progPatchOf(p: Partial<GovProgram>): Partial<GovFeedRow['program'] & object> {
  const o: Record<string, unknown> = {};
  if (p.name !== undefined) o.name = p.name;
  if (p.report_name !== undefined) o.reportName = p.report_name;
  if (p.region !== undefined) o.regions = toList(p.region);
  if (p.region_detail !== undefined) o.regionDetails = toList(p.region_detail);
  if (p.category !== undefined) o.category = toList(p.category);
  if (p.amount !== undefined) o.amount = p.amount;
  if (p.amount_max_manwon !== undefined) o.amountMaxManwon = p.amount_max_manwon;
  if (p.apply_start !== undefined) o.applyStart = p.apply_start;
  if (p.deadline !== undefined) o.deadline = p.deadline;
  if (p.first_come !== undefined) o.firstCome = p.first_come;
  if (p.recur_yearly !== undefined) o.recurYearly = p.recur_yearly;
  if (p.target_company_scale !== undefined) o.companyScale = p.target_company_scale;
  if (p.target_startup !== undefined) o.startup = p.target_startup;
  if (p.target_yrs_min !== undefined) o.yrsMin = p.target_yrs_min;
  if (p.target_yrs_max !== undefined) o.yrsMax = p.target_yrs_max;
  if (p.target_ceo_age_max !== undefined) o.ceoAgeMax = p.target_ceo_age_max;
  return o as Partial<GovFeedRow['program'] & object>;
}

/** 대상 한 줄 — 규모 · 창업 · 업력 · 나이 (없으면 빈 문자열 = 제한 없음) */
export function targetOf(p: FeedProg): string {
  const yrs = p.yrsMin != null && p.yrsMax != null ? `업력 ${yrsTxt(p.yrsMin)}~${yrsTxt(p.yrsMax)}`
    : p.yrsMax != null ? `${yrsTxt(p.yrsMax)} 이내` : p.yrsMin != null ? `${yrsTxt(p.yrsMin)} 이상` : '';
  return [p.companyScale, p.startup === '창업기업만' ? '창업' : p.startup === '비창업만' ? '비창업' : '', yrs,
    p.ceoAgeMax != null ? `${p.ceoAgeMax}세 이하` : ''].filter(Boolean).join(' · ');
}

/** 'YYYY-MM-DD' → 'YY.MM.DD' (같은 해 둘째 날짜는 'MM.DD') */
export function shortDate(d: string | null | undefined, sameYearAs?: string | null): string {
  if (!d) return '';
  const m = String(d).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return '';
  if (sameYearAs && String(sameYearAs).slice(0, 4) === m[1]) return `${m[2]}.${m[3]}`;
  return `${m[1].slice(2)}.${m[2]}.${m[3]}`;
}

/** 지난 회차 날짜를 옮겨 온 「내년 예상」인지 — 마감이 최신 회차 마감보다 뒤이고 최신 회차는 이미 지났다 */
export function isProjected(r: GovFeedRow, p: FeedProg): boolean {
  return !!(r.latestEnd && p.deadline && r.latestEnd < today() && p.deadline > r.latestEnd);
}

/** '최대 1억 5천만원' → 15000 (만원) · 못 읽으면 null */
export function parseManwon(text: string): number | null {
  const t = String(text || '').replace(/,/g, '');
  const rx = /(?:(\d+(?:\.\d+)?)\s*억)?\s*(?:(\d+(?:\.\d+)?)\s*천)?\s*(?:(\d+(?:\.\d+)?)\s*만)?/g;
  let m: RegExpExecArray | null;
  while ((m = rx.exec(t)) !== null) {
    if (m[0] === '') { rx.lastIndex += 1; continue; }
    if (m[1] || m[2] || m[3]) {
      const v = Number(m[1] || 0) * 10000 + Number(m[2] || 0) * 1000 + Number(m[3] || 0);
      return v > 0 ? Math.round(v) : null;
    }
  }
  const won = t.match(/(\d{5,})\s*원/);
  return won ? Math.round(Number(won[1]) / 10000) || null : null;
}

/** 만원 → '180만원' · '2.4억' */
export function fmtManwon(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(Number(v))) return '';
  const n = Number(v);
  if (n >= 10000) { const e = n / 10000; return `${Number.isInteger(e) ? e : e.toFixed(e < 10 ? 1 : 0).replace(/\.0$/, '')}억`; }
  return `${n.toLocaleString()}만원`;
}

// ── 팝오버 (표 칸 인라인 편집) ────────────────────────────────────
export function FeedPopover({ anchor, width = 360, onClose, children }: {
  anchor: DOMRect; width?: number; onClose: () => void; children: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; maxH: number }>({ top: anchor.bottom + 4, left: anchor.left, maxH: 400 });
  useLayoutEffect(() => {
    const vw = window.innerWidth; const vh = window.innerHeight;
    const h = ref.current ? ref.current.offsetHeight : 300;
    const left = Math.max(8, Math.min(anchor.left, vw - width - 8));
    const below = vh - anchor.bottom - 12;
    const above = anchor.top - 12;
    if (h <= below || below >= above) setPos({ top: anchor.bottom + 4, left, maxH: Math.max(160, below) });
    else setPos({ top: Math.max(8, anchor.top - 4 - Math.min(h, above)), left, maxH: above });
  }, [anchor, width]);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const down = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) closeRef.current(); };
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); closeRef.current(); } };
    const t = setTimeout(() => document.addEventListener('mousedown', down), 0);
    document.addEventListener('keydown', key, true);
    return () => { clearTimeout(t); document.removeEventListener('mousedown', down); document.removeEventListener('keydown', key, true); };
  }, []);
  return createPortal(
    <div ref={ref} data-feed-popover="1" style={{
      position: 'fixed', top: pos.top, left: pos.left, width, maxHeight: pos.maxH, overflowY: 'auto', zIndex: 1300,
      background: C.white, border: `1px solid ${C.border}`, borderRadius: R.md, boxShadow: '0 10px 30px rgba(17,24,39,0.16)',
      padding: 12, boxSizing: 'border-box',
    }}>{children}</div>,
    document.body,
  );
}

/** 팝오버 아래 [취소] [저장] */
export function PopoverFoot({ onCancel, onSave, saving, hint }: { onCancel: () => void; onSave: () => void; saving?: boolean; hint?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 12, paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
      <span style={{ flex: 1, fontSize: T.xs, color: C.muted }}>{hint}</span>
      <button onClick={onCancel} style={{ padding: '6px 12px', borderRadius: R.sm, border: `1px solid ${C.border}`, background: C.white, color: C.sub, fontSize: T.sm, fontWeight: 600, cursor: 'pointer' }}>취소</button>
      <button onClick={onSave} disabled={saving} style={{ padding: '6px 14px', borderRadius: R.sm, border: 'none', background: C.accent, color: C.white, fontSize: T.sm, fontWeight: 700, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1 }}>{saving ? '저장 중…' : '저장'}</button>
    </div>
  );
}

// ── AI 사용량 ───────────────────────────────────────────────────
export const USAGE_KIND: Record<string, string> = { screen: '선별', analyze: '분석', analyze_batch: '분석(일괄·50%)', learn: '학습', targets: '대상 판정', targets_batch: '대상 판정(일괄·50%)' };   // 대상 판정 = 하위 업종 · 전용 대상 AI 다시 읽기 (2026-10-05o)
export const fmtUsd = (v: number | null | undefined) => `$${(Number(v) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const fmtTok = (v: number | null | undefined) => { const x = Number(v) || 0; return x >= 1e6 ? `${(x / 1e6).toFixed(1)}M` : x >= 1e3 ? `${Math.round(x / 1e3).toLocaleString()}K` : String(x); };
/** 툴팁 한 덩이 — 용도별 호출·비용 */
export function usageLines(u: GovFeedUsage | null | undefined): string {
  if (!u) return '기록 없음';
  const kinds = Object.entries(u.byKind || {}).filter(([, v]) => v.calls > 0)
    .map(([k, v]) => `${USAGE_KIND[k] || k} ${v.calls.toLocaleString()}회 · ${fmtUsd(v.estUsd)}`);
  return [...kinds, `합계 ${u.calls.toLocaleString()}회 · 입력 ${fmtTok(u.inTok)} · 출력 ${fmtTok(u.outTok)} 토큰 · 약 ${fmtUsd(u.estUsd)}`].join('\n');
}
